import React from "react";
import {
  Center,
  Flex,
  Heading,
  Text,
  useDisclosure,
  VStack,
  Box,
  HStack,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  GridContainer,
  Spinner,
  columnTypes,
  distinctValue,
  isMobile,
  returnOnPremium,
} from "../../components";
import { useEffect, useState } from "react";
import { LicenseManager } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react"; // AG Grid Component
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import "./PositionGrid.css";
import { usePositionController } from "./usePositionController";
import { PositionDetailsView } from "./position-details/PositionDetailsView";
import { RowDoubleClickedEvent, RowSelectedEvent } from "ag-grid-community";
import { useGridContextMenuController } from "../../components/grid/utils/useGridContextMenuController";
import { PositionsInnerRender } from "./grid/PositionsInnerRender";
import mixpanel from "mixpanel-browser";
import { useTour } from "@reactour/tour";
import { progressTour } from "../../components/tutorial/util/progressTour";
import { useGridState } from "../../components/grid/useGridState";
import { Toolbar } from "../../components/toolbar/Toolbar";
import {
  KnowledgeBaseHelperTopics,
  Position,
  ToolbarFeatures,
} from "../../types";
import { FaRegLightbulb } from "react-icons/fa";
import Uploads from "../uploads/Uploads";
import ExpirePositionModal from "../../components/grid/utils/contextMenu/ExpirePositionModal";
import ClosePositionModal from "../../components/grid/utils/contextMenu/ClosePositionModal";
import SearchGroupsModal from "../../components/grid/utils/contextMenu/SearchGroupsModal";
import { RiRoadMapFill } from "react-icons/ri";
import ReferAFriendPopup from "../referafriend/ReferAFriendPopup";
import RenderNoPositions from "../../components/dashboard/RenderNoPositions";
import { QuickFilter } from "../quick-filter/QuickFilter";
import { track, EVENTS } from "../../utils/analytics";
import { PositionStatus } from "../../types";
import { useAtomValue } from "jotai";
import { AccountAtom, TransactionsAtom, ViewModeAtom } from "../../store";
import { simplePositionsColumnDefinitions } from "./simplePositionsColumnDefinitions";
import { useFilters } from "../../hooks/useFilters";
import { useTransactions } from "../../hooks/useTransactions";

LicenseManager.setLicenseKey(
  "Using_this_AG_Grid_Enterprise_key_( AG-042988 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Option Tracker Ltd )_is_granted_a_( Single Application )_Developer_License_for_the_application_( Option Tracker )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( Option Tracker )_need_to_be_licensed___( Option Tracker )_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 26 June 2024 )____[v2]_MTcxOTM1NjQwMDAwMA==da26b5a51856f363e61b37e5775314c6"
);

const Positions = () => {
  const viewMode = useAtomValue(ViewModeAtom);
  const isSimple = viewMode === "simple";
  const { isOpen, currentStep, steps, setIsOpen, setCurrentStep, setSteps } =
    useTour();
  const filters = useFilters();
  const hasSetSimpleDefaultRef = React.useRef(false);

  const [showPositionDetails, setShowPositionDetails] = useState(false);
  const [tradeIds, setTradeIds] = useState<number[]>([]);
  const [cashflowIds, setCashflowIds] = useState<number[]>([]);
  const [selectedParams, setSelectedParams] = useState<any>({});
  const controller = usePositionController();
  const [selectedPositions, setSelectedPositions] = useState<Position[]>([]);
  const transactions = useTransactions();
  const accountState = useAtomValue(AccountAtom);
  const transactionsState = useAtomValue(TransactionsAtom);

  const expandAll = () => {
    try {
      const api = controller.gridApiRef?.current?.api;
      if (api?.expandAll) {
        api.expandAll();
      } else if (api?.forEachNode) {
        api.forEachNode((node: any) => node.setExpanded(true));
      }
    } catch {}
  };
  const collapseAll = () => {
    try {
      const api = controller.gridApiRef?.current?.api;
      if (api?.collapseAll) {
        api.collapseAll();
      } else if (api?.forEachNode) {
        api.forEachNode((node: any) => node.setExpanded(false));
      }
    } catch {}
  };
  const determineIds = (rowIds: number[]) => {
    if (rowIds.length > 0) {
      const result = controller.getTradeAndCashflowIds(rowIds);
      setTradeIds(result.tradeIds);
      setCashflowIds(result.cashflowIds);
    }
  };

  const onRowSelected = (e: RowSelectedEvent) => {
    if (e.node.data && e.node.data.id) {
      determineIds([e.node.data.id]);

      // Track position selection event
      track(EVENTS.POSITION.POSITION_SELECTED, {
        position_id: e.node.data.id,
        position_name: e.node.data.name || "Unknown",
        is_group: e.node.group || false,
      });
    }
  };

  const openPane = (params: RowDoubleClickedEvent, dontOpen: boolean) => {
    if (!params.node.isSelected()) {
      return;
    }

    if (params.node.group) {
      const ids = params.node.allLeafChildren.map((x) => x.data.id);
      setSelectedPositions(
        controller.positions.filter((x) => ids.includes(x.id))
      );
      determineIds(ids);

      if (!dontOpen) {
        setShowPositionDetails(true);

        // Track position detail viewed for a group
        track(EVENTS.POSITION.POSITION_DETAIL_VIEWED, {
          position_count: ids.length,
          is_group: true,
          group_name: params.node.key || "Unknown Group",
        });
      }
    } else if (params.node.data && params.node.data.id) {
      determineIds([params.node.data.id]);

      setSelectedPositions(
        controller.positions.filter((x) => x.id === params.node.data.id)
      );

      if (!dontOpen) {
        setShowPositionDetails(true);

        // Track position detail viewed for a single position
        track(EVENTS.POSITION.POSITION_DETAIL_VIEWED, {
          position_id: params.node.data.id,
          position_name: params.node.data.name || "Unknown",
          is_group: false,
        });
      }
    }

    setSelectedParams(params);
  };

  useEffect(() => {
    if (localStorage.getItem("token") === null) {
      window.location.href = "/app/login";
    }
  }, []);

  useEffect(() => {
    if (isSimple && !hasSetSimpleDefaultRef.current) {
      // Set default to OPEN only once upon entering simple mode
      if (filters.positionStatus === PositionStatus.ALL) {
        filters.setPositionStatus(PositionStatus.OPEN);
      }
      hasSetSimpleDefaultRef.current = true;
    }
  }, [isSimple]);

  // Refetch positions when switching between simple and advanced mode
  useEffect(() => {
    if (accountState?.selectedAccount?.id) {
      transactions.refresh(accountState.selectedAccount.id);
    }
  }, [viewMode]);

  return (
    <Flex h="full" direction="column" w="full">
      <Toolbar
        enabledOptions={[
          ToolbarFeatures.FILTER_SELECTOR,
          ToolbarFeatures.STATUS,
          ...(isSimple ? [] : [ToolbarFeatures.GROUPING]),
          ToolbarFeatures.HELP,
          ToolbarFeatures.PRICING_QUEUE,
          ToolbarFeatures.TITLE,
          ToolbarFeatures.DESCRIPTION,
          ToolbarFeatures.AUTOSYNC,
        ]}
        title={"Positions"}
        icon={<RiRoadMapFill />}
        description="
                View and manage your positions. Double on a position to view trades and right-click to access more options."
        helpKey={KnowledgeBaseHelperTopics.POSITIONS}
        positionsController={controller}
        onAutoAssignStrategies={isSimple ? () => {
              transactions.autoAssignGroups(transactionsState.trades.filter((trade) => trade.id), accountState.selectedAccount?.id as any, "POSITION_GROUP");
        } : undefined}
        autoAssignButtonText={isSimple ? "Auto-assign strategies to groups" : undefined}
        extraActions={(
          <HStack spacing={2}>
            <Tooltip label="Expand all">
              <IconButton aria-label="Expand all" size="sm" variant="outline" icon={<ChevronDownIcon />} onClick={expandAll} />
            </Tooltip>
            <Tooltip label="Collapse all">
              <IconButton aria-label="Collapse all" size="sm" variant="outline" icon={<ChevronUpIcon />} onClick={collapseAll} />
            </Tooltip>
          </HStack>
        )}
      />
      <QuickFilter />
      <ReferAFriendPopup />

      {controller.positions.length === 0 ? (
        <RenderNoPositions />
      ) : (
        <Flex flex={1} px={2}>
          <Allotment vertical separator={true}>
            <GridContainer>
              <>
                {controller.switchingColDef === true && (
                  <Center h="70%">
                    <VStack>
                      <Spinner />
                      <Text fontSize="14px">Switching layout...</Text>
                    </VStack>
                  </Center>
                )}
                {controller.switchingColDef === false && (
                  <>
                    <AGGridWrapper
                      controller={controller}
                      openPane={openPane}
                      onRowSelected={onRowSelected}
                      currentStep={currentStep}
                      setCurrentStep={setCurrentStep}
                      setIsOpen={setIsOpen}
                    />
                  </>
                )}
              </>
            </GridContainer>

            <Allotment.Pane
              visible={showPositionDetails}
              preferredSize={400}
              minSize={200}
            >
              <PositionDetailsView
                onClose={() => setShowPositionDetails(false)}
                params={selectedParams}
                tradeIds={tradeIds}
                cashflowIds={cashflowIds}
                positions={selectedPositions}
              />
            </Allotment.Pane>
          </Allotment>
        </Flex>
      )}
    </Flex>
  );
};

const AGGridWrapper = (props: any) => {
  const viewMode = useAtomValue(ViewModeAtom);
  const isSimple = viewMode === "simple";

  const gridState = useGridState({
    gridName: `Positions-${props.controller.grouping}-${isSimple ? "Simple" : "Advanced"}`,
  });

  const {
    isOpen: isExpireModalOpen,
    onOpen: onOpenExpireModal,
    onClose: onCloseExpireModal,
  } = useDisclosure();

  const {
    isOpen: isCloseModalOpen,
    onOpen: onOpenCloseModal,
    onClose: onCloseCloseModal,
  } = useDisclosure();

  const {
    isOpen: isBookSearchModalOpen,
    onOpen: onOpenBookSearchModal,
    onClose: onCloseBookSearchModal,
  } = useDisclosure();

  const {
    isOpen: isPositionGroupSearchModalOpen,
    onOpen: onOpenPositionGroupSearchModal,
    onClose: onClosePositionGroupSearchModal,
  } = useDisclosure();

  const [closingPrice, setClosingPrice] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [closingFees, setClosingFees] = useState("");
  const [closingCommission, setClosingCommission] = useState("");
  const [closingNotes, setClosingNotes] = useState("");

  const {
    getContextMenuItems,
    handleExpire,
    handleClose,
    handleBookSelect,
    handlePositionGroupSelect,
    openBookSearchModal: openBookSearchModalFn,
    openPositionGroupSearchModal: openPositionGroupSearchModalFn,
    freshBooks,
    freshGroupNames,
  } = useGridContextMenuController(
    onOpenExpireModal,
    onCloseExpireModal,
    onOpenCloseModal,
    onCloseCloseModal,
    () => {},
    () => {},
    onOpenBookSearchModal,
    onCloseBookSearchModal,
    onOpenPositionGroupSearchModal,
    onClosePositionGroupSearchModal
  );

  // Create wrapper functions to handle the drag and drop search modal opening
  const handleOpenBookSearchModal = (params: any) => {
    openBookSearchModalFn(params);
  };

  const handleOpenPositionGroupSearchModal = (params: any) => {
    openPositionGroupSearchModalFn(params);
  };

  // Override the onRowDragEnd function to include our search modal functions
  const onRowDragEnd = (params: any) => {
    props.controller.onRowDragEnd(
      params,
      handleOpenBookSearchModal,
      handleOpenPositionGroupSearchModal
    );
  };

  const handleConfirmExpire = () => {
    handleExpire(closingFees, closingCommission, closingNotes);
    onCloseExpireModal();
  };

  const handleConfirmClose = () => {
    handleClose(
      closingPrice,
      closingDate,
      closingFees,
      closingCommission,
      closingNotes
    );
    onCloseCloseModal();
  };

  // Get transactions from the controller to pass to the search modals
  const transactions = props.controller.transactions;

  const handleGridReady = (e: any) => {
    gridState.onGridReady(e);
    if (isSimple) {
      try {
        e.api.expandAll();
      } catch {}
    }
  };

  const handleFirstDataRendered = () => {
    gridState.onFirstDataRendered();
    if (isSimple && props.controller.gridApiRef?.current) {
      try {
        const api = props.controller.gridApiRef.current.api;
        api.expandAll();

        // Apply default sorts if none are set yet
        const colState = api.getColumnState?.() || [];
        const hasAnySort = colState.some((c: any) => !!c.sort);
        if (!hasAnySort) {
          const availableIds = new Set(colState.map((c: any) => c.colId));
          const valueSortId = availableIds.has("marketValue")
            ? "marketValue"
            : availableIds.has("unrealisedPnl")
            ? "unrealisedPnl"
            : availableIds.has("unRealisedPnL")
            ? "unRealisedPnL"
            : undefined;

          const sortState: any[] = [];
          if (valueSortId) {
            sortState.push({ colId: valueSortId, sort: "desc", sortIndex: 0 });
          }
          if (availableIds.has("lastTradeDate")) {
            sortState.push({ colId: "lastTradeDate", sort: "desc", sortIndex: 1 });
          }
          if (sortState.length > 0) {
            api.applyColumnState({ state: sortState, applyOrder: false });
          }
        }
      } catch {}
    }
  };

  return (
    <>
      <AgGridReact
        key={`Positions-${props.controller.grouping}-${isSimple ? "Simple" : "Advanced"}`}
        autoGroupColumnDef={{
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: PositionsInnerRender,
          },
          width: 600,
          sortable: !isSimple,
          resizable: !isSimple,
          headerName: isSimple ? "Group" : "Book",
        }}
        rowData={props.controller.positions} // Row Data for Rows
        columnDefs={isSimple ? simplePositionsColumnDefinitions : props.controller.columnDefs} // Column Defs for Columns
        columnTypes={columnTypes} // Optional - custom column types
        // rowSelection="single" // Options - allows click selection of rows
        rowSelection="multiple" // Options - allows click selection of rows
        rowHeight={isSimple ? 40 : 31}
        headerHeight={isSimple ? 40 : undefined}
        showOpenedGroup={true}
        suppressAggFilteredOnly={false}
        suppressAggFuncInHeader={true}
        ref={props.controller.gridApiRef}
        sideBar={isSimple ? undefined : {
          toolPanels: [
            {
              id: "columns",
              labelDefault: "Columns",
              labelKey: "columns",
              iconKey: "columns",
              toolPanel: "agColumnsToolPanel",
              toolPanelParams: {
                suppressPivotMode: true,
                suppressValues: true,
                suppressRowGroups: true,
              },
            },
          ],
          position: "left",
          defaultToolPanel: "filters",
        }}
        suppressMovableColumns={isSimple}
        rowDragEntireRow={isMobile}
        suppressChangeDetection={false}
        getContextMenuItems={getContextMenuItems}
        reactiveCustomComponents={true}
        aggFuncs={{
          distinct: distinctValue,
          returnOnPremium: returnOnPremium,
          none: () => {
            return "";
          },
        }}
        onRowGroupOpened={(params) => {
          progressTour(
            params as any,
            props.setCurrentStep,
            props.currentStep,
            props.setIsOpen
          );

          if (
            window.location.href.includes("tutorial=true") ||
            window.location.href.includes("result=paid")
          ) {
            if (params.node.level === 2) {
              props.openPane(params);
            }
          }

          gridState.performGroupChanged(params);
        }}
        rowDragManaged={false}
        rowDragMultiRow={true}
        getRowId={props.controller.getRowId}
        onRowSelected={(e: RowSelectedEvent) => {
          props.onRowSelected(e);
          // openPane(e, true);
        }}
        onRowDoubleClicked={(params: RowDoubleClickedEvent) => {
          props.openPane(params, false);
        }}
        loadingOverlayComponent={props.controller.loadingOverlayComponent}
        loadingOverlayComponentParams={
          props.controller.loadingOverlayComponentParams
        }
        noRowsOverlayComponent={props.controller.noRowsOverlayComponent}
        noRowsOverlayComponentParams={
          props.controller.noRowsOverlayComponentParams
        }
        onRowDragMove={props.controller.onRowDragMove}
        onRowDragEnd={onRowDragEnd}
        animateRows={false}
        onGridReady={handleGridReady}
        onFirstDataRendered={handleFirstDataRendered} // Disabled as breaks grouping...
        onColumnEverythingChanged={gridState.onColumnChanged}
        onColumnResized={gridState.onColumnResized}
        onColumnMoved={gridState.onColumnMoved}
        onSortChanged={gridState.onColumnChanged}
      />

      <ExpirePositionModal
        isOpen={isExpireModalOpen}
        onClose={onCloseExpireModal}
        onConfirm={handleConfirmExpire}
        closingFees={closingFees}
        setClosingFees={setClosingFees}
        closingCommission={closingCommission}
        setClosingCommission={setClosingCommission}
        closingNotes={closingNotes}
        setClosingNotes={setClosingNotes}
      />
      <ClosePositionModal
        isOpen={isCloseModalOpen}
        onClose={onCloseCloseModal}
        onConfirm={handleConfirmClose}
        closingPrice={closingPrice}
        setClosingPrice={setClosingPrice}
        closingDate={closingDate}
        setClosingDate={setClosingDate}
        closingFees={closingFees}
        setClosingFees={setClosingFees}
        closingCommission={closingCommission}
        setClosingCommission={setClosingCommission}
        closingNotes={closingNotes}
        setClosingNotes={setClosingNotes}
      />
      <SearchGroupsModal
        isOpen={isBookSearchModalOpen}
        onClose={onCloseBookSearchModal}
        title="Search Books"
        items={freshBooks.length > 0 ? freshBooks : (transactions?.books || [])}
        onSelect={handleBookSelect}
      />
      <SearchGroupsModal
        isOpen={isPositionGroupSearchModalOpen}
        onClose={onClosePositionGroupSearchModal}
        title="Search Position Groups"
        items={freshGroupNames.length > 0 ? freshGroupNames : (transactions?.groupNames || [])}
        onSelect={handlePositionGroupSelect}
      />
    </>
  );
};

const NoPositionsOverlay = () => {
  return (
    <Center h="70%">
      <VStack>
        <Box mb="10px">
          <FaRegLightbulb size="50" />
        </Box>
        <Heading size="md">No Positions Found</Heading>
        <Text maxW="300px" textAlign={"center"} mb="20px">
          Please add your trades to see positions.
        </Text>
        <Uploads />
      </VStack>
    </Center>
  );
};

export default Positions;
